import { createFileRoute, redirect } from '@tanstack/react-router';
import Header from '@/components/Header/Header';
import { FormattedMessage, useIntl } from 'react-intl';
import Button from '@/components/core/Button';
import Input from '@/components/core/Input';
import { FilterPanel } from '@/components/FilterPanel';
import { useState, useMemo, useCallback, useEffect } from 'react';
import { Loading } from '@/components/Loading';
import { ViewPermissions } from '@/components/Auth/ViewPermissions';
import { checkUserPermissions } from '@/components/Auth/checkUserPermissions';
import { FilteredListContainer } from '@/components/FilteredListContainer';
import { VirtualizedList } from '@/components/VirtualizedList.tsx';
import { RightPanel } from '@/components/RightPanel/RightPanel';
import { useAscOrDescIcon } from '@/hooks/useSort.tsx';
import { SystemAdministratorListItem } from '@/components/SystemAdministrators/SystemAdministratorListItem';
import { SearchFilterParams, SortDirection } from '@/types/filterSearch';
import { createSortConfig } from '@/utils/sort';
import { useSearchFilterSort } from '@/hooks/useSearchFilterSort';
import { ActionButtons } from '@/components/Header/ActionButtons';
import { IconPlus } from '@/icons';
import { DialogWindow } from '@/components/Dialog';
import { AddSystemAdministratorDialog } from '@/components/SystemAdministrators/AddSystemAdministratorDialog';
import { useListTenantUsers, UserDTO } from '@/api/trafikportalen';
import { DocumentHead } from '@/components/core/DocumentHead';
import { useAuth } from '@/stores/auth';

export const Route = createFileRoute(
  '/$tenantId/_layout/system-administrators/',
)({
  component: SystemAdministrators,
  validateSearch: (search: Record<string, unknown>): SearchFilterParams => {
    return {
      query: search.query as string,
      sort: search.sort as string,
      direction: search.direction as SortDirection,
    };
  },
  beforeLoad: ({ params }) => {
    const hasPermission = checkUserPermissions({
      permission: 'tenant:viewer',
    });
    if (!hasPermission) {
      throw redirect({ to: '/$tenantId', params });
    }
  },
});

function SystemAdministrators() {
  const { query, sort, direction } = Route.useSearch();
  const { formatMessage } = useIntl();
  const getIcon = useAscOrDescIcon();
  const { userProfile } = useAuth();

  const [addAdminOpen, setAddAdminOpen] = useState<boolean>(false);
  const [announcement, setAnnouncement] = useState('');

  const { data: administrators, isLoading } = useListTenantUsers();

  const sortConfigs = useMemo(() => {
    return createSortConfig<UserDTO>({
      textFields: ['firstname', 'lastname', 'email'],
    });
  }, []);

  const {
    filterSearch,
    sortedItems,
    setFilterSearch,
    handleSort,
    handleSearch,
  } = useSearchFilterSort<UserDTO>({
    items: administrators || [],
    searchKeys: ['firstname', 'lastname', 'email'],
    sortConfigs,
    routePath: Route.fullPath,
    defaultSearchParams: {
      sort: 'firstname',
      direction: 'asc',
    },
  });

  const renderAdministratorItem = useCallback(
    (administrator: UserDTO) => {
      return (
        <SystemAdministratorListItem
          key={administrator.id}
          administrator={administrator}
          currentUserEmail={userProfile?.email}
        />
      );
    },
    [userProfile?.email],
  );

  // Announce changes to sort and filter for screen readers
  useEffect(() => {
    if (!filterSearch || !sortedItems) return;

    const count = sortedItems.length;
    const sortLabel =
      filterSearch.sort === 'firstname'
        ? formatMessage({ defaultMessage: 'förnamn' })
        : filterSearch.sort === 'lastname'
          ? formatMessage({ defaultMessage: 'efternamn' })
          : formatMessage({ defaultMessage: 'e-post' });
    const directionLabel =
      filterSearch.direction === 'asc'
        ? formatMessage({ defaultMessage: 'stigande' })
        : formatMessage({ defaultMessage: 'fallande' });

    const message = formatMessage(
      {
        defaultMessage:
          '{count} {count, plural, one {systemadministratör} other {systemadministratörer}} sorterade efter {sortLabel} {directionLabel}',
      },
      { count, sortLabel, directionLabel },
    );

    setAnnouncement(message);
  }, [filterSearch, sortedItems.length, formatMessage, sortedItems]);

  useEffect(() => {
    if (filterSearch) return;

    setFilterSearch({
      query: query ?? '',
      sort: sort ?? 'firstname',
      direction: direction ?? 'asc',
    });
  }, [direction, query, sort, filterSearch, setFilterSearch]);

  if (isLoading) {
    return <Loading />;
  }

  return (
    <>
      <DocumentHead
        titleKey="page.systemAdministrators.title"
        descriptionKey="page.systemAdministrators.description"
      />
      <Header
        title={<FormattedMessage defaultMessage="Systemadministratörer" />}
        description={
          <FormattedMessage defaultMessage="Lista över alla systemadministratörer" />
        }
        actions={
          <ViewPermissions permission="tenant:editor">
            <ActionButtons>
              <Button
                startIcon={<IconPlus />}
                onClick={() => setAddAdminOpen(true)}
                variant="outline"
              >
                <FormattedMessage defaultMessage="Lägg till systemadministratör" />
              </Button>
            </ActionButtons>
          </ViewPermissions>
        }
      />
      <FilteredListContainer>
        <div aria-live="polite" aria-atomic="true" className="sr-only">
          {announcement}
        </div>
        <RightPanel>
          {filterSearch && (
            <FilterPanel
              firstInput={
                <Input
                  type="text"
                  label="Sök"
                  placeholder="Sök namn eller e-postadress"
                  id="administrator-search"
                  value={filterSearch.query}
                  onChange={(e) => handleSearch(e.target.value)}
                  aria-description="Sök efter systemadministratörer genom att skriva deras namn eller avdelning"
                />
              }
              sortingOptions={[
                <Button
                  variant={
                    filterSearch.sort === 'firstname' ? 'filled' : 'outline'
                  }
                  startIcon={getIcon({
                    selectedSort: filterSearch.sort!,
                    wantedSort: 'firstname',
                    sortDirection: filterSearch.direction!,
                  })}
                  onClick={() => handleSort('firstname')}
                  aria-label={`Sortera efter förnamn ${filterSearch.direction === 'asc' ? 'stigande' : 'fallande'}`}
                  aria-pressed={filterSearch.sort === 'firstname'}
                >
                  <FormattedMessage defaultMessage="Förnamn" />
                </Button>,
                <Button
                  variant={
                    filterSearch.sort === 'lastname' ? 'filled' : 'outline'
                  }
                  startIcon={getIcon({
                    selectedSort: filterSearch.sort!,
                    wantedSort: 'lastname',
                    sortDirection: filterSearch.direction!,
                  })}
                  onClick={() => handleSort('lastname')}
                  aria-label={`Sortera efter efternamn ${filterSearch.direction === 'asc' ? 'stigande' : 'fallande'}`}
                  aria-pressed={filterSearch.sort === 'lastname'}
                >
                  <FormattedMessage defaultMessage="Efternamn" />
                </Button>,
              ]}
            />
          )}
        </RightPanel>
        <VirtualizedList
          count={sortedItems.length}
          countName="systemadministratörer"
          totalItems={administrators || []}
          filteredItems={sortedItems}
          renderItem={renderAdministratorItem}
          getItemKey={(administrator) => administrator.id}
        />
      </FilteredListContainer>
      <DialogWindow
        title={formatMessage({
          defaultMessage: 'Lägg till systemadministratör',
        })}
        content={
          <AddSystemAdministratorDialog
            onClose={() => setAddAdminOpen(false)}
          />
        }
        onOpenChange={setAddAdminOpen}
        open={addAdminOpen}
      />
    </>
  );
}
